// 📦 SEARCH-WORK 2026-07-02 — KEEP but dormant. Small, tested, correct; the ~20-line key
//    helper a future /search may use. No live consumer yet. See core/SEARCH_WORK_STATUS.md.
/**
 * core/normalization.js — the shared truth function (SEARCH substrate).
 *
 * Pure, stateless, entity-agnostic. Turns messy multilingual human input into ONE
 * canonical key so downstream layers can match "the same thing written differently."
 * It never touches a DB, never knows what entity the input is, never resolves
 * identity, never ranks. See core/NORMALIZATION_SPEC.md for the contract and
 * core/NORMALIZATION_ADVERSARIAL_SUITE.md for the test corpus this satisfies.
 *
 * Two lexical formats, chosen by the CALLER (never sniffed from content):
 *   normalize(input, 'text')  -> name/word key   | UNNORMALIZABLE
 *   normalize(input, 'phone') -> E.164 phone key  | UNNORMALIZABLE
 *
 * Invariants enforced here:
 *  - Totality: a valid input -> exactly one key; anything else -> UNNORMALIZABLE.
 *    Never a "best-effort partial key" (non-admissible chars REJECT, never strip).
 *  - Distribution-independence: the ASCII key is an invisible internal join token,
 *    chosen because ASCII is the interchange substrate — no script is "primary".
 *  - Idempotence: normalize(normalize(x)) === normalize(x).
 *  - Recall bias: aspirate/ejective pairs collapse on purpose; safe because equal
 *    keys are match CANDIDATES, not asserted identities (resolver is Core's, deferred).
 *
 * NOTE: the tricky character sets below are built from explicit code points so this
 * source file stays pure ASCII (no invisible characters living in the repo).
 *
 * Run:  node core/normalization.test.mjs
 */

// Uppercase sentinel: text keys are always lowercased, so this can never collide
// with a real key.
export const UNNORMALIZABLE = 'UNNORMALIZABLE'

// Georgian (Mkhedruli) -> ASCII skeleton. Derived from the national romanization,
// deliberately collapsing the aspirate/ejective pairs users never type
// (Ta/Te->t, Ka/Qe->k, Pa/Fe->p, Cha/Ch'e->ch, Tsa/Ts'e->ts). Recall-biased fold.
const GE = {
  'ა':'a','ბ':'b','გ':'g','დ':'d','ე':'e','ვ':'v',
  'ზ':'z','თ':'t','ი':'i','კ':'k','ლ':'l','მ':'m',
  'ნ':'n','ო':'o','პ':'p','ჟ':'zh','რ':'r','ს':'s',
  'ტ':'t','უ':'u','ფ':'p','ქ':'k','ღ':'gh','ყ':'q',
  'შ':'sh','ჩ':'ch','ც':'ts','ძ':'dz','წ':'ts','ჭ':'ch',
  'ხ':'kh','ჯ':'j','ჰ':'h',
}

// Cyrillic (Russian, as used for Georgian names) -> ASCII skeleton. Applied per
// character (also the homoglyph defense: Cyrillic o U+043E != Latin o U+006F by
// codepoint, but folds to the same 'o'). Hard/soft signs fold to nothing.
const CY = {
  'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e',
  'ё':'e','ж':'zh','з':'z','и':'i','й':'i','к':'k',
  'л':'l','м':'m','н':'n','о':'o','п':'p','р':'r',
  'с':'s','т':'t','у':'u','ф':'f','х':'kh','ц':'ts',
  'ч':'ch','ш':'sh','щ':'sh','ъ':'','ы':'y','ь':'',
  'э':'e','ю':'yu','я':'ya',
}

const cp = (c) => String.fromCharCode(c)

// Zero-width / format controls that survive NFKC and must be stripped explicitly
// (NOT the same as whitespace — these join, they do not separate):
//   200B-200D zero-width, FEFF BOM, 00AD soft hyphen, 200E/200F & 202A-202E
//   directional marks, 2060 word joiner.
const INVISIBLE_CODES = [
  0x200b, 0x200c, 0x200d, 0xfeff, 0x00ad,
  0x200e, 0x200f, 0x202a, 0x202b, 0x202c, 0x202d, 0x202e, 0x2060,
]
const INVISIBLE = new RegExp(
  '[' + INVISIBLE_CODES.map((c) => '\\u' + c.toString(16).padStart(4, '0')).join('') + ']',
  'g',
)

// Intra-token separators for the text format: stripped (join), not spaced.
//   hyphens/dashes: 002D 2010-2015 2212 · apostrophes: 0027 2018 2019 02BC 0060
const IGNORABLE = new Set(
  [0x2d, 0x2010, 0x2011, 0x2012, 0x2013, 0x2014, 0x2015, 0x2212,
   0x27, 0x2018, 0x2019, 0x02bc, 0x60].map(cp),
)
const EXCEL_APOSTROPHE = cp(0x27)

const isSpace = (ch) => /\s/.test(ch)

/**
 * normalizeText(input) -> canonical key | UNNORMALIZABLE
 * Per-character script fold (Latin/Georgian/Cyrillic) -> Latin equivalence fold.
 */
export function normalizeText(input) {
  if (typeof input !== 'string') return UNNORMALIZABLE

  let s = input.normalize('NFKC').toLowerCase()
  s = s.replace(INVISIBLE, '')
  s = s.normalize('NFD').replace(/\p{M}+/gu, '').normalize('NFC') // strip diacritics

  const out = []
  for (const ch of s) {
    if (isSpace(ch)) { out.push(' '); continue }
    if (IGNORABLE.has(ch)) { continue }
    if (ch >= 'a' && ch <= 'z') { out.push(ch); continue }
    if (GE[ch] !== undefined) { out.push(GE[ch]); continue }
    if (CY[ch] !== undefined) { out.push(CY[ch]); continue }
    return UNNORMALIZABLE // non-admissible char -> reject, NEVER strip-and-salvage (totality)
  }

  let t = out.join('')
  // Latin-side equivalence fold (the tunable, recall-biased stage). Digraphs first.
  t = t.replace(/ph/g, 'p').replace(/th/g, 't').replace(/dj/g, 'j')
  t = t.replace(/f/g, 'p').replace(/w/g, 'v').replace(/q/g, 'k').replace(/y/g, 'i')
  t = t.replace(/([aeiou])\1+/g, '$1') // collapse doubled vowels
  t = t.replace(/\s+/g, ' ').trim()

  return t === '' ? UNNORMALIZABLE : t
}

/**
 * normalizePhone(input) -> E.164 key | UNNORMALIZABLE
 *
 * MINIMAL Georgia-only canonicalizer. The SPEC delegates this to libphonenumber
 * (region GE); it is hand-rolled here only because no phone library is available
 * in this environment. Swap for libphonenumber when one is — the contract
 * (variants -> one E.164; unresolvable -> UNNORMALIZABLE) is what matters.
 */
export function normalizePhone(input) {
  if (typeof input !== 'string') return UNNORMALIZABLE

  let s = input.normalize('NFKC').trim().replace(INVISIBLE, '')
  if (s.startsWith(EXCEL_APOSTROPHE)) s = s.slice(1)  // Excel text-guard apostrophe
  if (/[a-zA-Z]/.test(s)) return UNNORMALIZABLE        // letters => not a phone (ext, E-notation, "abc")

  const hadPlus = s.startsWith('+')
  let d = s.replace(/\D/g, '')
  if (d === '') return UNNORMALIZABLE

  let intl = hadPlus
  if (!intl && d.startsWith('00')) { intl = true; d = d.slice(2) } // 00 international prefix

  let national
  if (intl) {
    if (!d.startsWith('995')) return UNNORMALIZABLE // only GE supported
    national = d.slice(3)
  } else if (d.length === 12 && d.startsWith('995')) {
    national = d.slice(3)
  } else {
    national = d.replace(/^0/, '') // strip a single national trunk 0
  }

  if (national.length !== 9) return UNNORMALIZABLE // GE national significant number is 9 digits
  return '+995' + national
}

/**
 * normalize(input, format) — the single entry point.
 * `format` is a LEXICAL choice supplied by the caller, never an entity and never
 * inferred from content. No entity/type/context parameter exists, by design.
 */
export function normalize(input, format) {
  if (format === 'text') return normalizeText(input)
  if (format === 'phone') return normalizePhone(input)
  throw new Error(`normalize: unknown format ${JSON.stringify(format)} (expected 'text' | 'phone')`)
}
